// Co-located CSS — Vite splits this into a separate chunk automatically
import '@/styles/syncfusion.css';

import { registerLicense } from '@syncfusion/ej2-base';
import { RichTextEditorComponent, Toolbar, Inject, Image, Link, HtmlEditor, QuickToolbar, Table, Count } from '@syncfusion/ej2-react-richtexteditor';
import { useEffect, useRef } from 'react';

import { config } from '@/lib/config';

// Register Syncfusion license on first import of this module (not at app init).
// License comes from runtime config (the #app-config island), so the build-once
// bundle isn't tied to a baked key and the license can rotate without a rebuild.
const syncfusionLicense = config.syncfusionLicense;
if (syncfusionLicense) {
  registerLicense(syncfusionLicense);
}

// =============================================================================
// Toolbar presets (#787)
// =============================================================================

// Full toolbar — the original everything-on configuration. TemplateFormDialog
// (the only pre-#787 consumer) keeps this by default; behavior is byte-identical
// to the pre-preset wrapper.
const FULL_TOOLBAR: { items: string[] } = {
  items: [
    'Bold', 'Italic', 'Underline', 'StrikeThrough', '|',
    'FontName', 'FontSize', 'FontColor', 'BackgroundColor', '|',
    'Formats', 'Alignments', '|',
    'OrderedList', 'UnorderedList', '|',
    'Indent', 'Outdent', '|',
    'CreateLink', 'Image', 'CreateTable', '|',
    'ClearFormat', '|',
    'Undo', 'Redo', '|',
    'SourceCode', 'FullScreen'
  ]
};

const FULL_QUICK_TOOLBAR = {
  image: ['Replace', 'Align', 'Caption', 'Remove', 'InsertLink', 'OpenImageLink', '-', 'EditImageLink', 'RemoveImageLink', 'Display', 'AltText', 'Dimension'],
  link: ['Open', 'Edit', 'UnLink']
};

/**
 * Restricted toolbar preset (#787) — offers ONLY formatting the server-side
 * sanitizer (RichTextSanitizer, #786) preserves:
 * `h2,h3,h4,p,br,strong,em,u,ul,ol,li,blockquote,a[href]`.
 *
 * Anything beyond this allowlist would be silently stripped on save — the
 * toolbar must not offer markup that cannot round-trip. Undo/Redo are editing
 * aids (they produce no HTML), so they are allowed. The client restriction is
 * UX only; the server allowlist remains the security authority.
 *
 * Named export so #788+ and future long-text fields reuse the same contract.
 */
export const RESTRICTED_RICH_TEXT_TOOLBAR: { type: 'MultiRow'; items: string[] } = {
  // MultiRow, not the default Expand: Expand measures toolbar width at init
  // and, when the editor mounts inside a container that is 0px wide at that
  // instant (tab panels, collapsible cards), exiles every item to a hidden
  // overflow popup — the toolbar renders 0px tall. MultiRow never uses the
  // overflow popup, so it is immune to init-time measurement.
  type: 'MultiRow',
  items: [
    'Formats', '|',
    'Bold', 'Italic', 'Underline', '|',
    'OrderedList', 'UnorderedList', '|',
    'CreateLink', '|',
    'ClearFormat', '|',
    'Undo', 'Redo'
  ]
};

/**
 * Restricted Formats dropdown (#787) — Paragraph + H2/H3/H4 only. No H1 (page
 * titles are owned by the listing template, and the sanitizer strips h1) and
 * no Preformatted (pre is not in the sanitizer allowlist). Items mirror EJ2's
 * built-in formatItems entries (cssClass/command/subCommand included) so the
 * dropdown behaves identically to the stock configuration, minus the removed
 * levels.
 */
export const RESTRICTED_RICH_TEXT_FORMATS: {
  default: string;
  width: string;
  types: Array<{ cssClass: string; text: string; command: string; subCommand: string; value: string }>;
} = {
  default: 'Paragraph',
  width: '65px',
  types: [
    { cssClass: 'e-paragraph', text: 'Paragraph', command: 'Formats', subCommand: 'P', value: 'P' },
    { cssClass: 'e-h2', text: 'Heading 2', command: 'Formats', subCommand: 'H2', value: 'H2' },
    { cssClass: 'e-h3', text: 'Heading 3', command: 'Formats', subCommand: 'H3', value: 'H3' },
    { cssClass: 'e-h4', text: 'Heading 4', command: 'Formats', subCommand: 'H4', value: 'H4' },
  ],
};

// No image quick toolbar in the restricted preset — images are not offered.
const RESTRICTED_QUICK_TOOLBAR = {
  link: ['Open', 'Edit', 'UnLink']
};

// Restricted paste hardening: strip media/table noise at paste time so the
// editing view better matches post-save reality. The server sanitizer (#786)
// remains the authority either way.
const RESTRICTED_PASTE_CLEANUP = {
  prompt: false,
  plainText: false,
  keepFormat: true,
  deniedTags: ['img', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'video', 'audio', 'iframe'],
};

const FULL_PASTE_CLEANUP = {
  prompt: false,
  plainText: false,
  keepFormat: true,
};

interface SyncfusionEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  readonly?: boolean;
  height?: string | number;
  className?: string;
  /**
   * Toolbar preset (#787). Defaults to 'full' so pre-existing consumers
   * (TemplateFormDialog) are untouched. 'restricted' limits the toolbar to
   * what the server sanitizer preserves — use it for dealer-authored
   * descriptions.
   */
  preset?: 'full' | 'restricted';
  /** Forwarded to the EJ2 root so `<Label htmlFor>` keeps its target. */
  id?: string;
  /** Accessible name for the editable region (WCAG 2.1 AA). */
  ariaLabel?: string;
}

export function SyncfusionEditor({
  value,
  onChange,
  placeholder = 'Start typing...',
  readonly = false,
  height = 400,
  className = '',
  preset = 'full',
  id,
  ariaLabel,
}: SyncfusionEditorProps) {
  const rteRef = useRef<RichTextEditorComponent>(null);

  const restricted = preset === 'restricted';
  const toolbarSettings = restricted ? RESTRICTED_RICH_TEXT_TOOLBAR : FULL_TOOLBAR;
  const quickToolbarSettings = restricted ? RESTRICTED_QUICK_TOOLBAR : FULL_QUICK_TOOLBAR;
  const pasteCleanupSettings = restricted ? RESTRICTED_PASTE_CLEANUP : FULL_PASTE_CLEANUP;

  const editorChange = () => {
    if (rteRef.current) {
      const content = rteRef.current.getHtml();
      onChange(content);
    }
  };

  useEffect(() => {
    // Ensure the value is synced when it changes externally
    if (rteRef.current && rteRef.current.getHtml() !== value) {
      rteRef.current.value = value;
    }
  }, [value]);

  return (
    <div className={className}>
      <RichTextEditorComponent
        ref={rteRef}
        id={id}
        value={value}
        change={editorChange}
        toolbarSettings={toolbarSettings}
        quickToolbarSettings={quickToolbarSettings}
        format={restricted ? RESTRICTED_RICH_TEXT_FORMATS : undefined}
        htmlAttributes={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
        placeholder={placeholder}
        readonly={readonly}
        height={height}
        enableResize={true}
        maxLength={50000}
        pasteCleanupSettings={pasteCleanupSettings}
      >
        <Inject services={[Toolbar, Image, Link, HtmlEditor, QuickToolbar, Table, Count]} />
      </RichTextEditorComponent>
    </div>
  );
}
